import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  DollarOutlined,
  EditOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { apiClient } from "../../../shared/api/AllApi";
import { formatCurrency, toApiNumber } from "../../../utils/formatters";
import { fetchSystemConfig, readCachedSystemConfig } from "../../../shared/lib/systemConfig";
import { fetchExchangeRates } from "../../../shared/lib/exchangeRates";
import usePermissions from "../../../hooks/usePermissions";
import "../../../shared/styles/ErpPages.css";

const computePaidMainEquivalent = (main, secondary, rateRow) => {
  const m = Math.max(0, toApiNumber(main, 0));
  const s = Math.max(0, toApiNumber(secondary, 0));
  if (s <= 0.000001) return Math.round(m * 100) / 100;
  const secPerMain = toApiNumber(rateRow?.main_per_secondary, 0);
  if (secPerMain <= 0) return Math.round(m * 100) / 100;
  const secondaryAsMain = Math.round((s / secPerMain) * 100) / 100;
  return Math.round((m + secondaryAsMain) * 100) / 100;
};

const poStatusColors = {
  draft: "default",
  ordered: "processing",
  received: "success",
  cancelled: "error",
  partial: "processing",
  paid: "success",
};

/**
 * ERP vendor master — overview + purchase orders (same shell as customer workspace).
 */
const VendorWorkspace = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission("vendors.index");

  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [vendor, setVendor] = useState(null);
  const [poRows, setPoRows] = useState([]);
  const [poLoading, setPoLoading] = useState(false);
  const [systemConfig, setSystemConfig] = useState(() => readCachedSystemConfig());

  const [editOpen, setEditOpen] = useState(false);
  const [form] = Form.useForm();

  const [payModalOpen, setPayModalOpen] = useState(false);
  const [payAmount, setPayAmount] = useState(0);
  const [payAmountMain, setPayAmountMain] = useState(0);
  const [payAmountSecondary, setPayAmountSecondary] = useState(0);
  const [exchangeRates, setExchangeRates] = useState([]);
  const [exchangeRateId, setExchangeRateId] = useState(null);
  const [paySubmitting, setPaySubmitting] = useState(false);

  const mainCode = systemConfig?.currency_code || "IQD";
  const secondaryCode = systemConfig?.secondary_currency_code || "";
  const useDualCurrency = Boolean(secondaryCode);
  const balance = toApiNumber(vendor?.balance_main, 0);

  const selectedRate = useMemo(() => {
    if (!exchangeRateId || !exchangeRates.length) return null;
    return exchangeRates.find((r) => Number(r.id) === Number(exchangeRateId)) || null;
  }, [exchangeRateId, exchangeRates]);

  const appliedPayMainTotal = useMemo(() => {
    if (!useDualCurrency) return Math.round(toApiNumber(payAmount, 0) * 100) / 100;
    return computePaidMainEquivalent(payAmountMain, payAmountSecondary, selectedRate);
  }, [payAmount, payAmountMain, payAmountSecondary, selectedRate, useDualCurrency]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cfg = await fetchSystemConfig();
      setSystemConfig(cfg);
      const res = await apiClient.get(`/vendors/${id}`);
      setVendor(res.data || null);
    } catch (e) {
      message.error(e?.response?.data?.message || t("loadError", "Failed to load"));
      setVendor(null);
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  const loadPos = useCallback(async () => {
    setPoLoading(true);
    try {
      const res = await apiClient.get(`/vendors/${id}/purchase-orders`, {
        params: { per_page: 50 },
      });
      setPoRows(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch {
      setPoRows([]);
      message.error(t("vendorsPoLoadFailed"));
    } finally {
      setPoLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (tab === "orders") loadPos();
  }, [tab, loadPos]);

  const openEdit = () => {
    if (!vendor) return;
    form.setFieldsValue({
      name: vendor.name,
      phone: vendor.phone || "",
      email: vendor.email || "",
      notes: vendor.notes || "",
    });
    setEditOpen(true);
  };

  const submitEdit = async () => {
    try {
      const values = await form.validateFields();
      await apiClient.put(`/vendors/${id}`, values);
      message.success(t("vendorsUpdated"));
      setEditOpen(false);
      load();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || t("vendorsSaveFailed"));
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: t("vendorsDeleteConfirm", "Archive this vendor?"),
      content: t(
        "vendorsSoftDeleteHint",
        "This is a soft delete. Purchase order history stays available."
      ),
      okType: "danger",
      okText: t("delete", "Delete"),
      cancelText: t("cancel", "Cancel"),
      onOk: async () => {
        try {
          await apiClient.delete(`/vendors/${id}`);
          message.success(t("vendorsDeleted", "Vendor archived."));
          navigate("/vendors");
        } catch (e) {
          message.error(e?.response?.data?.message || t("vendorsDeleteFailed"));
        }
      },
    });
  };

  const openPay = () => {
    if (balance <= 0.0001) {
      message.info(t("vendorsNoBalanceToPay"));
      return;
    }
    const rounded = Math.round(balance * 100) / 100;
    setPayAmount(rounded);
    setPayAmountMain(rounded);
    setPayAmountSecondary(0);
    setPayModalOpen(true);
  };

  useEffect(() => {
    if (!payModalOpen || !useDualCurrency) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await fetchExchangeRates();
        if (cancelled) return;
        const rows = Array.isArray(list) ? list : [];
        setExchangeRates(rows);
        setExchangeRateId(rows[0]?.id ?? null);
      } catch {
        if (!cancelled) {
          setExchangeRates([]);
          setExchangeRateId(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [payModalOpen, useDualCurrency]);

  const submitPayment = async () => {
    if (!vendor) return;
    setPaySubmitting(true);
    try {
      const body = useDualCurrency
        ? {
            amount_main: payAmountMain,
            amount_secondary: payAmountSecondary,
            exchange_rate_id: exchangeRateId || undefined,
          }
        : { amount: payAmount };
      await apiClient.post(`/vendors/${id}/record-payment`, body);
      message.success(t("vendorsPaymentSuccess"));
      setPayModalOpen(false);
      load();
      if (tab === "orders") loadPos();
    } catch (e) {
      const d = e?.response?.data;
      let msg = d?.message || t("vendorsPaymentFailed");
      if (d?.applied_main_equivalent != null && d?.balance_main != null) {
        msg = `${msg} — ${t("vendorsPaymentAppliedVsOwed", {
          applied: formatCurrency(d.applied_main_equivalent, mainCode),
          owed: formatCurrency(d.balance_main, mainCode),
        })}`;
      }
      message.error(msg);
    } finally {
      setPaySubmitting(false);
    }
  };

  const poColumns = [
    {
      title: "ID",
      dataIndex: "id",
      width: 72,
      render: (poId) => <Link to={`/purchase-orders/${poId}/edit`}>#{poId}</Link>,
    },
    {
      title: t("purchaseOrdersColReference"),
      dataIndex: "reference",
      ellipsis: true,
      render: (v) => v || "—",
    },
    {
      title: t("date"),
      dataIndex: "created_at",
      width: 150,
      render: (v) => (v ? dayjs(v).format("YYYY-MM-DD HH:mm") : "—"),
    },
    {
      title: t("purchaseOrdersColTotal"),
      dataIndex: "total_price",
      align: "right",
      render: (v) => formatCurrency(v, mainCode),
    },
    {
      title: t("purchaseOrdersColPaid"),
      dataIndex: "amount_paid",
      align: "right",
      render: (v) => formatCurrency(v, mainCode),
    },
    {
      title: t("purchaseOrdersColOutstanding"),
      dataIndex: "outstanding",
      align: "right",
      render: (v) => formatCurrency(v, mainCode),
    },
    {
      title: t("purchaseOrdersColStatus"),
      dataIndex: "status",
      render: (s) => (
        <Tag color={poStatusColors[s] || "default"}>{t(`poStatus_${s}`, { defaultValue: s })}</Tag>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="erp-page" style={{ textAlign: "center", paddingTop: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="erp-page">
        <div className="erp-shell" style={{ padding: 24 }}>
          <p>{t("noDataFound", "No records")}</p>
          <Button onClick={() => navigate("/vendors")}>{t("back", "Back")}</Button>
        </div>
      </div>
    );
  }

  const overview = (
    <div>
      <div className="erp-kv">
        <div className="k">{t("id", "ID")}</div>
        <div className="v">{vendor.id}</div>
        <div className="k">{t("name", "Name")}</div>
        <div className="v">{vendor.name || "—"}</div>
        <div className="k">{t("phoneNumber", "Phone")}</div>
        <div className="v">{vendor.phone || "—"}</div>
        <div className="k">{t("email", "Email")}</div>
        <div className="v">{vendor.email || "—"}</div>
        <div className="k">{t("vendorsNotes", "Notes")}</div>
        <div className="v">{vendor.notes || "—"}</div>
        <div className="k">{t("openBalance", "Open balance")}</div>
        <div className="v">
          <span className={`erp-money ${balance > 0 ? "debt" : "ok"}`}>
            {formatCurrency(balance, mainCode)}
          </span>
        </div>
        <div className="k">{t("vendorsPoCount", "Purchase orders")}</div>
        <div className="v">{vendor.purchase_orders_count ?? poRows.length}</div>
      </div>

      <div className="erp-actions-row">
        <Button type="primary" onClick={() => setTab("orders")}>
          {t("vendorsPoHistoryTitle", "Purchase orders")}
        </Button>
        {canManage && (
          <Button icon={<DollarOutlined />} onClick={openPay} disabled={balance <= 0.0001}>
            {t("vendorsPay", "Record payment")}
          </Button>
        )}
        {canManage && (
          <Button icon={<EditOutlined />} onClick={openEdit}>
            {t("edit", "Edit")}
          </Button>
        )}
        {canManage && (
          <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
            {t("delete", "Delete")}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="erp-page">
      <div className="erp-shell">
        <div className="erp-header">
          <div>
            <div className="erp-breadcrumb">
              <Button
                type="link"
                size="small"
                icon={<ArrowLeftOutlined />}
                style={{ padding: 0 }}
                onClick={() => navigate("/vendors")}
              >
                {t("vendorsTitle", "Vendors")}
              </Button>
              {" / "}
              {vendor.name}
            </div>
            <h1>{vendor.name}</h1>
          </div>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                load();
                if (tab === "orders") loadPos();
              }}
            >
              {t("refresh", "Refresh")}
            </Button>
          </Space>
        </div>

        <div className="erp-meta">
          <span>
            {t("openBalance", "Open balance")}:{" "}
            <b className={`erp-money ${balance > 0 ? "debt" : "ok"}`}>
              {formatCurrency(balance, mainCode)}
            </b>
          </span>
          <span>
            {t("status", "Status")}:{" "}
            <b>
              {balance > 0.0001 ? t("open", "Open") : t("settled", "Settled")}
            </b>
          </span>
        </div>

        <div className="erp-body" style={{ padding: 14 }}>
          <Tabs
            activeKey={tab}
            onChange={setTab}
            items={[
              { key: "overview", label: t("overview", "Overview"), children: overview },
              {
                key: "orders",
                label: t("vendorsPoHistoryTitle", "Purchase orders"),
                children: (
                  <Table
                    size="small"
                    rowKey="id"
                    loading={poLoading}
                    columns={poColumns}
                    dataSource={poRows}
                    pagination={{ pageSize: 25 }}
                    locale={{ emptyText: t("vendorsPoEmpty") }}
                    scroll={{ x: 700 }}
                  />
                ),
              },
            ]}
          />
        </div>
      </div>

      <Modal
        title={t("vendorsEditTitle")}
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        onOk={submitEdit}
        okText={t("save", "Save")}
        cancelText={t("cancel", "Cancel")}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label={t("purchaseOrdersVendorName")} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="phone" label={t("purchaseOrdersVendorPhone")}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label={t("purchaseOrdersVendorEmail")}>
            <Input type="email" />
          </Form.Item>
          <Form.Item name="notes" label={t("vendorsNotes")}>
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t("vendorsPayModalTitle")}
        open={payModalOpen}
        onCancel={() => setPayModalOpen(false)}
        onOk={submitPayment}
        confirmLoading={paySubmitting}
        okText={t("vendorsPaySubmit")}
        destroyOnHidden
        width={useDualCurrency ? 440 : 400}
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <div>
            {vendor.name} — {t("vendorsOutstanding")}:{" "}
            <strong>{formatCurrency(balance, mainCode)}</strong>
          </div>
          {useDualCurrency ? (
            <>
              <div>
                <div style={{ marginBottom: 4 }}>{t("poAmountPaidMain", { code: mainCode })}</div>
                <InputNumber
                  min={0}
                  style={{ width: "100%" }}
                  value={payAmountMain}
                  onChange={(v) => setPayAmountMain(v ?? 0)}
                />
              </div>
              <div>
                <div style={{ marginBottom: 4 }}>
                  {t("poAmountPaidSecondary", { code: secondaryCode })}
                </div>
                <InputNumber
                  min={0}
                  style={{ width: "100%" }}
                  value={payAmountSecondary}
                  onChange={(v) => setPayAmountSecondary(v ?? 0)}
                />
              </div>
              {exchangeRates.length > 0 ? (
                <Select
                  style={{ width: "100%" }}
                  value={exchangeRateId}
                  onChange={setExchangeRateId}
                  options={exchangeRates.map((r) => ({
                    value: r.id,
                    label: `${String(r.effective_from || "").slice(0, 10)} — 1 ${mainCode} = ${r.main_per_secondary} ${secondaryCode}`,
                  }))}
                />
              ) : null}
              <div>
                {t("Total applied")} ({mainCode}):{" "}
                <strong>{formatCurrency(appliedPayMainTotal, mainCode)}</strong>
              </div>
            </>
          ) : (
            <InputNumber
              min={0.01}
              max={Math.round(balance * 100) / 100}
              style={{ width: "100%" }}
              value={payAmount}
              onChange={(v) => setPayAmount(v ?? 0)}
            />
          )}
        </Space>
      </Modal>
    </div>
  );
};

export default VendorWorkspace;
